import {DataStatus, Form, FormField} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isNil} from 'ramda';

import {useGetBranchListQuery} from '@dms/api/core';
import {useGetWarehousesQuery} from '@dms/api/warehouse';
import {i18n} from '@dms/i18n';
import {getOptionsFromBranches, getOptionsFromWarehouses} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {FormToDataGridConnectorProps} from 'features/eag-grid';

type MaterialArticleTabExternalFiltersForm = Partial<{
  branchId: string;
  warehouseId: string;
  isAvailable: boolean;
}>;

type MaterialArticleTabExternalFiltersProps = {
  serviceOrderVariantId: string | Nullish;
  dataGridKey?: string;
} & FormToDataGridConnectorProps &
  TestIdProps;

export function MaterialArticleTabExternalFilters(props: MaterialArticleTabExternalFiltersProps) {
  const {
    data: warehouses,
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useGetWarehousesQuery(
    {
      serviceOrderVariantId: props.serviceOrderVariantId as string,
    },
    {skip: isNil(props.serviceOrderVariantId)}
  );

  const {
    data: branches,
    isLoading: branchesLoading,
    isError: isBranchesError,
  } = useGetBranchListQuery();

  const isLoading = isWarehousesLoading || branchesLoading;
  const isError = isWarehousesError || isBranchesError;

  // Force form re-render when values change to fix react-select state sync issues
  const formKey = JSON.stringify(props.values);

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<MaterialArticleTabExternalFiltersForm>
        key={`${props.dataGridKey}-${formKey}`}
        values={props.values}
        onChange={props.onChange}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="branchId"
              type="choice"
              label={i18n.t('entity.branch.labels.branch')}
              options={getOptionsFromBranches(branches)}
              data-testid={suffixTestId('branch', props)}
            />
            <FormField
              control={control}
              name="warehouseId"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.warehouse')}
              options={getOptionsFromWarehouses(warehouses)}
              data-testid={suffixTestId('warehouse', props)}
            />
            <FormField
              control={control}
              name="isAvailable"
              label={i18n.t('entity.warehouse.labels.availableOnly')}
              type="checkbox"
              data-testid={suffixTestId('availableOnly', props)}
            />
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
